/**
 * Authentication Context for Backend JWT Authentication
 * Replaces Auth0Provider with custom backend token management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { logger } from '../utils/logger';

// User type matching backend response
interface User {
  id: number;
  email: string;
  full_name: string;
  role: string;
}

// Auth context value type
interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (idToken: string) => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  canAccessAdminPanel: () => boolean;
  getUserRoles: () => string[];
  getUserProfile: () => User | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Authentication Provider Component
 * Wraps app and provides authentication state/functions
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * Initialize auth state by validating token with backend on mount
   * This prevents false-positive authentication from stale localStorage data
   */
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    logger.log('[Auth] Mount - checking token:', token ? `${token.substring(0, 20)}...` : 'none');

    if (!token) {
      logger.log('[Auth] No token found, setting isLoading=false');
      setIsLoading(false);
      return;
    }

    logger.log('[Auth] Token found, validating with backend...');

    // Validate token by fetching current user from backend
    authApi.getCurrentUser()
      .then((userData) => {
        logger.log('[Auth] Token validation SUCCESS:', userData);
        // Token is valid, update user state
        setUser(userData);
        // Also update localStorage with fresh user data
        localStorage.setItem('user', JSON.stringify(userData));
      })
      .catch((error) => {
        logger.error('[Auth] Token validation FAILED:', error.message);
        logger.log('[Auth] Clearing auth state due to validation failure');
        // Token is invalid or expired, clear auth state
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => {
        logger.log('[Auth] Validation complete, setting isLoading=false');
        setIsLoading(false);
      });
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string) => {
    logger.log('[Auth] Login attempt for:', email);
    setIsLoading(true);
    try {
      const response = await authApi.login(email, password);
      logger.log('[Auth] Login successful, token received:', response.token.substring(0, 20) + '...');
      logger.log('[Auth] User data:', response.user);

      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      logger.log('[Auth] Token and user saved to localStorage');

      setUser(response.user);
      logger.log('[Auth] User state updated, navigating to /');
      navigate('/');
    } catch (error) {
      logger.error('[Auth] Login FAILED:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  /**
   * Login with Google OAuth
   */
  const googleLogin = useCallback(async (idToken: string) => {
    logger.log('[Auth] Google login attempt');
    setIsLoading(true);
    try {
      const response = await authApi.googleLogin(idToken);
      logger.log('[Auth] Google login successful, token received:', response.token.substring(0, 20) + '...');
      logger.log('[Auth] User data:', response.user);

      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      logger.log('[Auth] Token and user saved to localStorage');

      setUser(response.user);
      logger.log('[Auth] User state updated, navigating to /');
      navigate('/');
    } catch (error) {
      logger.error('[Auth] Google login FAILED:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  /**
   * Logout and clear authentication
   */
  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  }, [navigate]);

  /**
   * Get user roles (currently single role from backend)
   */
  const getUserRoles = useCallback((): string[] => {
    if (!user) return [];
    return [user.role];
  }, [user]);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role: string): boolean => {
    return getUserRoles().includes(role);
  }, [getUserRoles]);

  /**
   * Check if user is admin
   */
  const isAdmin = useCallback((): boolean => {
    return hasRole('admin');
  }, [hasRole]);

  /**
   * Check if user can access admin panel
   */
  const canAccessAdminPanel = useCallback((): boolean => {
    return hasRole('admin') || hasRole('operator');
  }, [hasRole]);

  /**
   * Get current user profile
   */
  const getUserProfile = useCallback((): User | null => {
    return user;
  }, [user]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    googleLogin,
    logout,
    hasRole,
    isAdmin,
    canAccessAdminPanel,
    getUserRoles,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 * Throws error if used outside AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/**
 * Hook to require authentication
 * Redirects to login if not authenticated
 */
export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isLoading, isAuthenticated, navigate]);

  return { isAuthenticated, isLoading };
}

/**
 * Hook to require admin role
 * Shows error if user doesn't have admin access
 */
export function useRequireAdmin() {
  const { isAuthenticated, isLoading, canAccessAdminPanel } = useAuth();
  const hasAccess = canAccessAdminPanel();

  return {
    isAuthenticated,
    isLoading,
    hasAccess,
    error: !isLoading && isAuthenticated && !hasAccess ? 'Access denied: Admin role required' : null,
  };
}
